'use client'

/**
 * "Continue learning" rail — the member's in-progress courses with a progress
 * bar, most-recently-accessed first. Each card links to the course page, which
 * resolves the exact resume lesson.
 *
 * Each card also carries a remove (×) control: a member who started a course by
 * mistake (or no longer wants it on their homepage) can take it off the rail.
 * Removing wipes their tracked progress for that course (confirmed first), but
 * the course itself is untouched — they can start it again any time. Renders
 * nothing once the list is empty.
 */

import { useState } from 'react'
import { Link } from '@/i18n/navigation'
import { GraduationCap, X, Loader2 } from 'lucide-react'
import type { CourseProgressCard } from '@/lib/personalization/queries'
import { optimizeSanityImageUrl } from '@/lib/sanity/client'
import { removeCourseFromLearning } from '@/app/[locale]/(public)/courses/[slug]/enroll-actions'

export function ResumeLearningRail({ courses }: { courses: CourseProgressCard[] }) {
  const [list, setList] = useState(courses)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleRemove(id: string) {
    setPendingId(id)
    setError(null)
    const res = await removeCourseFromLearning(id)
    if (res.ok) {
      setList((cur) => cur.filter((c) => c.id !== id))
      setConfirmId(null)
    } else {
      setError('Could not remove the course — please try again.')
    }
    setPendingId(null)
  }

  if (list.length === 0) return null

  return (
    <section className="mx-auto w-full max-w-7xl px-4 py-8">
      <div className="mb-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-400">
          Pick up where you left off
        </p>
        <h2 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">Continue learning</h2>
      </div>

      {error && (
        <p className="mb-3 text-sm font-medium text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      <div className="-mx-4 flex snap-x gap-5 overflow-x-auto px-4 pb-4 [scrollbar-width:thin]">
        {list.map((course) => (
          <div key={course.id} className="relative w-[300px] shrink-0 snap-start">
            <Link
              href={course.href}
              className="group relative flex h-full flex-col overflow-hidden bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06),_0_10px_30px_rgba(0,0,0,0.05)] ring-1 ring-gray-200/70 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_40px_rgba(30,115,190,0.2),_0_1px_3px_rgba(0,0,0,0.06)] hover:ring-brand-300 dark:bg-gray-900 dark:ring-gray-800 dark:hover:ring-brand-700"
            >
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-gradient-to-br from-green-800 via-green-600 to-emerald-500">
                {course.coverUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={optimizeSanityImageUrl(course.coverUrl, 800)}
                    alt=""
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-white/70">
                    <GraduationCap className="h-10 w-10" />
                  </div>
                )}
                <span className="absolute left-3 top-3 inline-flex items-center gap-1 bg-green-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                  <GraduationCap className="h-3 w-3" />
                  Course
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h3 className="line-clamp-2 text-base font-semibold leading-tight text-gray-900 transition-colors group-hover:text-brand-700 dark:text-white dark:group-hover:text-brand-400">
                  {course.title}
                </h3>
                <div className="mt-auto pt-4">
                  <div className="flex items-center justify-between text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span>{course.percent}% complete</span>
                    <span className="font-semibold text-brand-700 group-hover:underline dark:text-brand-400">
                      {course.percent > 0 ? 'Continue' : 'Start'} →
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                    <div
                      className="h-full rounded-full bg-brand-600"
                      style={{ width: `${course.percent}%` }}
                    />
                  </div>
                </div>
              </div>
            </Link>

            {/* Remove control — a sibling of the Link (not inside it), so clicking
                it never navigates. Always visible so it's reachable on touch. */}
            {confirmId !== course.id && (
              <button
                type="button"
                onClick={() => {
                  setError(null)
                  setConfirmId(course.id)
                }}
                aria-label={`Remove ${course.title} from your list`}
                className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-colors hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <X className="h-4 w-4" />
              </button>
            )}

            {/* Confirmation — covers the card; destructive action is explicit. */}
            {confirmId === course.id && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-gray-900/95 p-5 text-center text-white">
                <p className="text-sm font-semibold">Remove &ldquo;{course.title}&rdquo;?</p>
                <p className="text-xs leading-relaxed text-gray-300">
                  You&rsquo;ll lose your tracked progress for this course, but you can start it again
                  anytime.
                </p>
                <div className="mt-1 flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleRemove(course.id)}
                    disabled={pendingId === course.id}
                    className="inline-flex items-center gap-1.5 bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
                  >
                    {pendingId === course.id && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
                    Remove
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmId(null)}
                    disabled={pendingId === course.id}
                    className="bg-white/15 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/25 disabled:opacity-60"
                  >
                    Keep
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
